import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Search, UserPlus, ChevronLeft, ChevronRight, Settings2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import useSettingsStore from '../../store/settingsStore';
import { dialog } from '../../store/dialogStore';
import { isOrgAdmin, hasCapability } from '../../utils/permissions';
import { fetchRoles, fetchAccessUsers, setUserRole } from '../../api/access';
import { roleColor, isOwnerRole } from '../../components/access/constants';
import AccessPersonCell from '../../components/access/AccessPersonCell';
import RoleCell from '../../components/access/RoleCell';
import WorkspacesCell from '../../components/access/WorkspacesCell';
import BoardsCell from '../../components/access/BoardsCell';
import InviteUserModal from '../../components/access/InviteUserModal';
import UserRowMenu from '../../components/access/UserRowMenu';

const PAGE_SIZE = 25;

// Planilha de gestão de usuários e acesso (Configurações → Usuários).
// Reaproveita o paradigma visual do quadro (arquitetura-v2.md §6): barra
// colorida do grupo à esquerda, badge de status para o grupo, chips +
// popover para áreas, contador + painel lateral para quadros.
export default function UsersPage() {
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);
  const createUser = useSettingsStore((s) => s.createUser);
  const updateUser = useSettingsStore((s) => s.updateUser);
  const deleteUser = useSettingsStore((s) => s.deleteUser);

  const isAdmin = isOrgAdmin(currentUser);
  const canAudit = isAdmin || hasCapability(currentUser, 'org.access.audit');
  const canAssignRole = isAdmin || hasCapability(currentUser, 'org.users.assign_role');
  const canManageWorkspaces = isAdmin || hasCapability(currentUser, 'workspace.members.manage') || hasCapability(currentUser, 'org.access.manage');
  const canManageBoards = isAdmin || hasCapability(currentUser, 'board.access.manage') || hasCapability(currentUser, 'org.boards.override');
  const canManageRoles = isAdmin || hasCapability(currentUser, 'org.roles.manage');
  const canInvite = isAdmin || hasCapability(currentUser, 'org.users.invite');
  const canManageUsers = isAdmin || hasCapability(currentUser, 'org.users.manage');

  const [roles, setRoles] = useState([]);
  const adminRole = useMemo(
    () => roles.find((r) => r.system_key === 'admin') || roles.find((r) => /admin/i.test(r.name || '')),
    [roles]
  );
  const onlyOneAdmin = (adminRole?.user_count ?? 0) === 1;

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [showInvite, setShowInvite] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    fetchRoles().then((data) => setRoles(Array.isArray(data) ? data : [])).catch(() => setRoles([]));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    fetchAccessUsers({ search, limit: PAGE_SIZE, offset })
      .then((data) => {
        setItems(data?.items || []);
        setTotal(data?.total || 0);
      })
      .catch((err) => setLoadError(err.message || 'Falha ao carregar usuários'))
      .finally(() => setLoading(false));
  }, [search, offset]);

  useEffect(() => { load(); }, [load]);

  // Debounce de 300ms na busca — evita 1 requisição por tecla.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setOffset(0);
      setSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const handleRoleChange = (row) => async (role) => {
    await setUserRole(row.user.id, role.id);
    load();
  };

  const handleActiveToggle = async (u) => {
    const isActive = u.active !== false;
    if (isActive) {
      const ok = await dialog.confirm(
        `Desativar ${u.name}`,
        'O usuário perderá acesso imediatamente, mas o histórico e as atribuições permanecem. Você pode reativar depois.',
        { confirmLabel: 'Desativar', danger: true }
      );
      if (!ok) return;
    }
    try {
      await updateUser(u.id, { active: !isActive });
      load();
    } catch (e) {
      dialog.alert('Erro', e.message);
    }
  };

  const handleResetPassword = async (u) => {
    const newPassword = await dialog.prompt(`Redefinir senha — ${u.name}`, {
      label: 'Nova senha',
      inputType: 'password',
      placeholder: 'Mínimo 6 caracteres',
      minLength: 6,
      confirmLabel: 'Redefinir',
    });
    if (!newPassword) return;
    try {
      await updateUser(u.id, { password: newPassword });
      await dialog.alert('Senha redefinida', `A nova senha de ${u.name} foi salva.`);
    } catch (e) {
      dialog.alert('Erro', e.message);
    }
  };

  const handleDelete = async (u) => {
    const ok = await dialog.confirm(
      `Remover ${u.name} permanentemente`,
      'Esta ação não pode ser desfeita. Se quiser apenas bloquear o acesso mantendo o histórico, use Desativar.',
      { confirmLabel: 'Remover', danger: true }
    );
    if (!ok) return;
    try {
      await deleteUser(u.id);
      load();
    } catch (e) {
      dialog.alert('Erro', e.message);
    }
  };

  const handleCreate = async (payload) => {
    await createUser(payload);
    load();
    fetchRoles().then((data) => setRoles(Array.isArray(data) ? data : [])).catch(() => {});
  };

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = Math.min(offset + PAGE_SIZE, total);

  if (!canAudit) {
    return (
      <div className="p-8 max-w-6xl mx-auto">
        <p className="text-sm text-text-secondary">Você não tem permissão para ver a gestão de usuários.</p>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-text-primary">Gestão de usuários</h2>
        <div className="flex items-center gap-2">
          {canManageRoles && (
            <button
              onClick={() => navigate('/settings/roles')}
              className="flex items-center gap-2 px-3 py-2 bg-main border border-border rounded-lg text-sm text-text-primary hover:border-accent/50 transition-colors"
            >
              <Settings2 className="w-4 h-4" />
              Gerir grupos
            </button>
          )}
          {canInvite && (
            <button
              onClick={() => setShowInvite(true)}
              className="flex items-center gap-2 px-4 py-2 bg-accent text-white rounded-lg text-sm font-medium hover:bg-accent/80 transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              Convidar
            </button>
          )}
        </div>
      </div>

      {onlyOneAdmin && (
        <div className="mb-4 px-4 py-3 bg-accent/10 border border-accent/30 rounded-lg text-sm text-text-primary">
          Você é o único Administrador desta organização. Considere adicionar outro.
        </div>
      )}

      <div className="mb-4 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Pesquisar nome ou e-mail"
            className="w-full bg-card border border-border rounded-lg pl-9 pr-3 py-2 text-sm text-text-primary outline-none focus:border-accent/50"
          />
        </div>
        <span className="text-xs text-text-secondary ml-auto">
          {total} {total === 1 ? 'pessoa' : 'pessoas'}
        </span>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-white/[0.02]">
            <tr className="text-left text-xs text-text-secondary border-b border-border/50">
              <th className="w-1 p-0"></th>
              <th className="px-4 py-3 font-medium">Pessoa</th>
              <th className="px-4 py-3 font-medium">Grupo</th>
              <th className="px-4 py-3 font-medium">Áreas de trabalho</th>
              <th className="px-4 py-3 font-medium">Quadros</th>
              <th className="px-4 py-3 font-medium w-12"></th>
            </tr>
          </thead>
          <tbody>
            {loadError && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-danger">
                  {loadError} · <button onClick={load} className="underline">Tentar de novo</button>
                </td>
              </tr>
            )}
            {!loadError && loading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-text-secondary">Carregando…</td></tr>
            )}
            {!loadError && !loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-text-secondary">Nenhum usuário encontrado.</td></tr>
            )}
            {!loadError && !loading && items.map((row) => {
              const u = row.user;
              const locked = isOwnerRole(row.role);
              const disableRoleEdit = !canAssignRole || (u.id === currentUser?.id && onlyOneAdmin);
              return (
                <tr key={u.id} className={`border-b border-border/30 hover:bg-white/[0.02] transition-colors ${u.active === false ? 'opacity-60' : ''}`}>
                  <td className="p-0">
                    <div className="h-full w-1 min-h-[52px]" style={{ backgroundColor: roleColor(row.role) }} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <AccessPersonCell user={u} />
                      {u.id === currentUser?.id && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent/15 text-accent font-medium shrink-0">você</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <RoleCell
                      user={u}
                      role={row.role}
                      roles={roles}
                      canEdit={!disableRoleEdit}
                      onChange={handleRoleChange(row)}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <WorkspacesCell
                      user={u}
                      workspaces={row.workspaces}
                      defaultLevel={roles.find((r) => r.id === row.role?.id)?.default_level}
                      canEdit={canManageWorkspaces}
                      onChanged={load}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <BoardsCell user={u} role={row.role} boards={row.boards} canEdit={canManageBoards} />
                  </td>
                  <td className="px-4 py-3">
                    {canManageUsers && (
                      <UserRowMenu
                        user={u}
                        currentUserId={currentUser?.id}
                        roleLocked={locked}
                        onResetPassword={handleResetPassword}
                        onToggleActive={handleActiveToggle}
                        onDelete={handleDelete}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-text-secondary">
            Mostrando {rangeStart}–{rangeEnd} de {total}
          </span>
          <div className="flex items-center gap-2">
            <button
              disabled={offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              className="p-1.5 rounded border border-border text-text-secondary hover:text-text-primary disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs text-text-secondary">{page} / {pageCount}</span>
            <button
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              className="p-1.5 rounded border border-border text-text-secondary hover:text-text-primary disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {showInvite && (
        <InviteUserModal
          roles={roles}
          onClose={() => setShowInvite(false)}
          onCreate={handleCreate}
        />
      )}
    </div>
  );
}
